import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  Switch,
  MenuItem,
  IconButton,
  Tooltip,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import DeleteIcon from '@material-ui/icons/Delete';
import ArchiveIcon from '@material-ui/icons/Archive';
import UnarchiveIcon from '@material-ui/icons/Unarchive';

import { makeStyles } from '@material-ui/core/styles';
import { apiBaseUrl } from '../../../utilities/configureApiFetch';

const useStyles = makeStyles((theme) => ({
  orangeFocused: {
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      backgroundColor: '#ffffff',
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderRadius: 14,
      borderColor: '#dddddd',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: '#FF7F27',
    },
    '& .MuiInputLabel-root': {
      color: '#666666',
      fontSize: '0.9rem',
    },
    '& .MuiInputLabel-outlined.Mui-focused': {
      color: '#FF7F27',
    },
    '& .MuiInputBase-input.Mui-disabled': {
      color: '#002D55',
      WebkitTextFillColor: '#002D55',
    },
  },
  dialogTitle: {
    borderBottom: '1px solid #eeeeee',
    padding: '16px 24px',
  },
  dialogTitleText: {
    color: '#002D55',
    fontWeight: 600,
    fontSize: '1.15rem',
  },
  dialogContent: {
    padding: '24px !important',
    overflow: 'auto',
    backgroundColor: '#e6eaf0',
  },
  contentSection: {
    marginBottom: theme.spacing(3),
  },
  sectionLabel: {
    fontSize: 12,
    color: '#666666',
    marginBottom: 8,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionPanel: {
    backgroundColor: '#f7faff',
    borderRadius: 18,
    padding: 16,
    border: '1px solid #dce8f5',
  },
  label: {
    color: '#002D55',
    fontWeight: 700,
    marginBottom: theme.spacing(1),
    fontSize: '0.875rem',
  },
  helperText: {
    color: '#4b5f75',
    fontSize: '0.78rem',
    marginTop: theme.spacing(1),
  },
  diagnosisInput: {
    width: '100%',
  },
  cancelButton: {
    backgroundColor: '#e6eaf0',
    color: '#002D55',
    fontWeight: 'bold',
    borderRadius: 50,
    border: '2px solid transparent',
    boxShadow: 'none',
    padding: '4px 16px',
    textTransform: 'none',
    minWidth: 100,
    '&:hover': {
      backgroundColor: '#e6eaf0',
      color: '#FF7F27',
      border: '2px solid #FF7F27',
      boxShadow: 'none',
    },
  },
  confirmButton: {
    backgroundColor: '#002D55',
    color: '#FFFFFF',
    borderRadius: 50,
    border: 'none',
    boxShadow: 'none',
    padding: '6px 16px',
    fontWeight: 600,
    textTransform: 'none',
    minWidth: 120,
    '&:hover': {
      backgroundColor: '#FF7F27',
      boxShadow: 'none',
    },
  },
  checkbox: {
    color: '#002D55',
    '&.Mui-checked': {
      color: '#FF7F27',
    },
  },
  checkboxLabel: {
    color: '#002D55',
    fontSize: '0.84rem',
    fontWeight: 600,
  },
  switchBase: {
    color: '#FF7F27',
    '&.Mui-checked': {
      color: '#FF7F27',
    },
    '&.Mui-checked + .MuiSwitch-track': {
      backgroundColor: '#FF7F27',
    },
  },
  switchTrack: {
    backgroundColor: '#bfbfbf',
  },
  switchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  switchLabel: {
    color: '#002D55',
    fontWeight: 700,
    fontSize: '0.875rem',
  },
  assessmentOptions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  noteBox: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: '#f5f7fb',
    borderRadius: 14,
    border: '1px solid #dce8f5',
  },
  errorText: {
    color: '#c62828',
    fontSize: '0.82rem',
    fontWeight: 600,
  },
  dialogActions: {
    padding: 16,
    gap: 8,
    justifyContent: 'space-between',
    borderTop: '1px solid #eeeeee',
    backgroundColor: '#ffffff',
  },
  actionGroup: {
    display: 'flex',
    gap: 8,
  },
  deleteButton: {
    color: '#c62828',
  },
}));

/**
 * AddTreatmentDialog - Dialog for adding a new treatment to a patient
 * 
 * Allows physio to:
 * 1. Enter a treatment name (any string like "shoulder", "knee pain", etc.)
 * 2. Optionally select a diagnosis template to clone default treatment from
 * 3. Or start with empty journal (None_Diagnosis style)
 * 
 * @param {object} props
 * @param {boolean} props.open - Dialog open state
 * @param {function} props.onClose - Close handler
 * @param {function} props.onSubmit - Submit handler (treatmentName, diagnosisIdentifier, autoGenerate)
 * @param {string} props.token - Auth token
 * @param {object} props.patient - Patient object with user_id
 */
const AddTreatmentDialog = ({ open, onClose, onSubmit, onDelete, onArchive, onReactivate, token, patient, mode = 'create', treatment = null, initialTreatmentName = '' }) => {
  const classes = useStyles();

  const complaintAreaOptions = [
    { value: 'a', label: 'Neck pain, headache or migraine' },
    { value: 'b', label: 'Shoulder pain' },
    { value: 'c', label: 'Elbow pain' },
    { value: 'd', label: 'Hand & wrist pain' },
    { value: 'e', label: 'Lower back pain' },
    { value: 'f', label: 'Middle back pain' },
    { value: 'g', label: 'Hip, thigh or groin pain' },
    { value: 'h', label: 'Knee pain' },
    { value: 'i', label: 'Ankle or foot pain' },
    { value: 'j', label: 'Symptoms after concussion' },
    { value: 'k', label: 'Symptoms after whiplash' },
    { value: 'l', label: 'Something else' },
  ];
  const complaintAreaByKey = complaintAreaOptions.reduce((areas, option) => {
    const keys = {
      a: 'neck_headache_migraine', b: 'shoulder', c: 'elbow', d: 'hand_wrist',
      e: 'lower_back', f: 'middle_back', g: 'hip_thigh_groin', h: 'knee',
      i: 'ankle_foot', j: 'concussion', k: 'whiplash', l: 'other',
    };
    areas[keys[option.value]] = option.value;
    return areas;
  }, {});

  const [treatmentName, setTreatmentName] = useState('');
  const [complaintArea, setComplaintArea] = useState('');
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);
  const [diagnosisInput, setDiagnosisInput] = useState('');
  const [diagnosesList, setDiagnosesList] = useState([]);
  const [loadingDiagnoses, setLoadingDiagnoses] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);
  const [sendAssessment, setSendAssessment] = useState(true);
  const [sendFullAssessment, setSendFullAssessment] = useState(true);
  const [sendShortAssessment, setSendShortAssessment] = useState(false);
  const [patientEmail, setPatientEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [archivedTreatments, setArchivedTreatments] = useState([]);
  const [archivedTreatmentsLoading, setArchivedTreatmentsLoading] = useState(false);
  const isEditMode = mode === 'edit';

  const loadArchivedTreatments = async () => {
    const patientId = patient?.user_id || patient?.id;
    if (!patientId || !token) {
      setArchivedTreatments([]);
      return;
    }

    setArchivedTreatmentsLoading(true);
    try {
      const response = await fetch(`${apiBaseUrl}/treatment/user/treatments/?patient=${encodeURIComponent(patientId)}&archived=1`, {
        cache: 'no-store',
        headers: { Authorization: `Token ${token}` },
      });
      if (!response.ok) {
        const responseData = await response.json().catch(() => ({}));
        throw new Error(responseData.error || responseData.detail || 'Failed to load archived treatment courses');
      }
      const responseData = await response.json();
      setArchivedTreatments(Array.isArray(responseData) ? responseData : []);
    } catch (loadError) {
      setArchivedTreatments([]);
      setError(loadError.message || 'Failed to load archived treatment courses');
    } finally {
      setArchivedTreatmentsLoading(false);
    }
  };

  // Fetch available diagnoses when dialog opens
  useEffect(() => {
    if (open && token) {
      setLoadingDiagnoses(true);
      fetch('https://api.virtus-one.com/treatment/diagnoses/', {
        headers: {
          'Authorization': `Token ${token}`,
          'Accept': 'application/json'
        }
      })
        .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch diagnoses'))
        .then(data => {
          // Extract diagnoses from response wrapper
          let diagnoses = [];
          if (Array.isArray(data)) {
            diagnoses = data;
          } else if (data && data.diagnoses && Array.isArray(data.diagnoses)) {
            diagnoses = data.diagnoses;
          } else if (data && data.results && Array.isArray(data.results)) {
            // Handle paginated response
            diagnoses = data.results;
          } else if (data && typeof data === 'object') {
            // Handle single object response, try to extract array
            diagnoses = Object.values(data).find(v => Array.isArray(v)) || [];
          }
          console.log('[AddTreatmentDialog] Fetched diagnoses:', diagnoses);
          setDiagnosesList(diagnoses || []);
          setLoadingDiagnoses(false);
        })
        .catch(err => {
          console.error('Error fetching diagnoses:', err);
          setDiagnosesList([]);
          setLoadingDiagnoses(false);
        });
    }
  }, [open, token]);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      const fallbackName = treatment?.name
        || treatment?.display_name
        || treatment?.diagnosis_title
        || treatment?.diagnosis?.title
        || treatment?.diagnosis?.diagnosis
        || '';
      setTreatmentName(isEditMode ? (initialTreatmentName || fallbackName) : '');
      setComplaintArea(isEditMode ? (complaintAreaByKey[treatment?.primary_complaint_key] || '') : '');
      setSelectedDiagnosis(null);
      setDiagnosisInput('');
      setAutoGenerate(false);
      setSendAssessment(true);
      setSendFullAssessment(true);
      setSendShortAssessment(false);
      setPatientEmail(patient?.email || '');
      setError('');
    }
  }, [open, patient, isEditMode, initialTreatmentName, treatment]);

  useEffect(() => {
    if (open && !isEditMode) {
      void loadArchivedTreatments();
    }
  }, [open, patient, token, isEditMode]);

  const handleSubmit = async () => {
    // Validation
    if (!treatmentName || !treatmentName.trim()) {
      setError('Please enter a treatment name');
      return;
    }

    if (sendAssessment && !sendFullAssessment && !sendShortAssessment) {
      setError('Please choose full or short assessment, or turn off sending');
      return;
    }

    if (sendAssessment && (sendFullAssessment || sendShortAssessment) && (!patientEmail || !patientEmail.trim())) {
      setError('Please enter an email address to send the assessment link');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      if (isEditMode) {
        await onSubmit({
          treatmentName: treatmentName.trim(),
          complaintArea,
          patient,
          treatment,
        });
      } else {
        // Use selected diagnosis identifier or None_Diagnosis if empty
        const diagnosisIdentifier = selectedDiagnosis?.diagnosis || 'None_Diagnosis';
        
        await onSubmit({
          treatmentName: treatmentName.trim(),
          diagnosisIdentifier,
          autoGenerate,
          complaintArea,
          patient,
          sendFullAssessment: sendAssessment ? sendFullAssessment : false,
          sendShortAssessment: sendAssessment ? sendShortAssessment : false,
          patientEmail: patientEmail.trim()
        });
      }

      // Close dialog on success
      onClose();
    } catch (err) {
      console.error('[AddTreatmentDialog] Submit error:', err);
      setError(err.message || (isEditMode ? 'Failed to update treatment' : 'Failed to add treatment'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setError('');
    onClose();
  };

  const handleDelete = async () => {
    if (!isEditMode || !onDelete || !window.confirm(`Delete ${treatmentName || 'this treatment'}? This cannot be undone.`)) return;
    setSubmitting(true);
    setError('');
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to delete treatment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleArchive = async () => {
    if (!isEditMode || !treatment || !onArchive || !window.confirm(`Archive ${treatmentName || 'this treatment'}? It can be reactivated later.`)) return;
    setSubmitting(true);
    setError('');
    try {
      await onArchive(treatment);
      onClose();
    } catch (archiveError) {
      setError(archiveError.message || 'Failed to archive treatment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReactivate = async (archivedTreatment) => {
    if (!onReactivate) return;
    setSubmitting(true);
    setError('');
    try {
      await onReactivate(archivedTreatment);
      await loadArchivedTreatments();
    } catch (reactivateError) {
      setError(reactivateError.message || 'Failed to reactivate treatment');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      keepMounted
      PaperProps={{ style: { borderRadius: 18 } }}
    >
      <DialogTitle className={classes.dialogTitle}>
        <Typography className={classes.dialogTitleText}>
          {isEditMode ? 'Rename Treatment' : 'Add New Treatment to Selected Patient'}
        </Typography>
      </DialogTitle>

      <DialogContent className={classes.dialogContent}>
        <Typography className={classes.sectionLabel}>Patient</Typography>
        <Box className={classes.sectionPanel} mb={3}>
          <Box className={classes.contentSection} mb={0}>
            <Typography className={classes.label}>
              Patient Name
            </Typography>
            <TextField
              fullWidth
              value={`${patient?.first_name || ''} ${patient?.last_name || ''}`.trim()}
              variant="outlined"
              className={classes.orangeFocused}
              disabled
            />
          </Box>
        </Box>

        <Typography className={classes.sectionLabel}>Treatment Details</Typography>
        <Box className={classes.sectionPanel} mb={3}>
          <Box className={classes.contentSection} mb={0}>
            <Typography className={classes.label}>
              Treatment Name *
            </Typography>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="e.g., shoulder, knee pain, lower back"
              value={treatmentName}
              onChange={(e) => setTreatmentName(e.target.value)}
              className={classes.orangeFocused}
              autoFocus
              error={!!error && !treatmentName.trim()}
            />
            <Typography className={classes.helperText}>
              Enter a clear label for this treatment, such as a body area or specific complaint.
            </Typography>
            <Box mt={2}>
              <Typography className={classes.label}>
                Area of Complaint
              </Typography>
              <TextField
                fullWidth
                select
                variant="outlined"
                value={complaintArea}
                onChange={(e) => setComplaintArea(e.target.value)}
                className={classes.orangeFocused}
              >
                <MenuItem value="">Use completed assessment if available</MenuItem>
                {complaintAreaOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <Typography className={classes.helperText}>
                This controls the initial exercise-library region for this treatment.
              </Typography>
            </Box>
          </Box>
        </Box>

        {!isEditMode && (
          <>
            {/* Optional Diagnosis Template Selection */}
                    {/*
                    <Box className={classes.contentSection}>
                      <Typography className={classes.label}>
                        Diagnosis Template (Optional)
                      </Typography>
                      {loadingDiagnoses ? (
                        <Box display="flex" alignItems="center" gap={1}>
                          <CircularProgress size={20} />
                          <Typography variant="body2">Loading diagnoses...</Typography>
                        </Box>
                      ) : (
                        <Autocomplete
                          options={Array.isArray(diagnosesList) ? diagnosesList : []}
                          getOptionLabel={(option) => {
                            if (!option) return '';
                            const parts = [];
                            if (option.diagnosis) parts.push(option.diagnosis);
                            if (option.icd10) parts.push(`(ICD-10: ${option.icd10})`);
                            return parts.join(' ');
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              placeholder="Select or type diagnosis..."
                              className={classes.diagnosisInput}
                            />
                          )}
                          value={selectedDiagnosis}
                          onChange={(event, newValue) => setSelectedDiagnosis(newValue)}
                          inputValue={diagnosisInput}
                          onInputChange={(event, newInputValue) => setDiagnosisInput(newInputValue)}
                          freeSolo={false}
                        />
                      )}
                      <Typography className={classes.helperText}>
                        Select a diagnosis template to copy default treatment plan and exercises
                      </Typography>
                    </Box>
            
                    {/* Auto-generate Treatment Option */}
                    {/*
                    {selectedDiagnosis && (
                      <Box className={classes.contentSection}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={autoGenerate}
                              onChange={(e) => setAutoGenerate(e.target.checked)}
                              className={classes.checkbox}
                            />
                          }
                          label={
                            <Typography variant="body2">
                              Auto-generate default treatment program and exercises
                            </Typography>
                          }
                        />
                        <Typography className={classes.helperText} style={{ marginLeft: 32 }}>
                          Creates a standard treatment plan based on the selected diagnosis template
                        </Typography>
                      </Box>
                    )}
                    */}
                    {/* Assessment Type Selection */}
                    <Typography className={classes.sectionLabel}>Assessment</Typography>
                    <Box className={classes.sectionPanel} mb={3}>
                      <Box className={classes.switchRow}>
                        <Typography className={classes.switchLabel}>
                          Send Assessment Link by Email?
                        </Typography>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={sendAssessment}
                              onChange={(e) => {
                                const next = e.target.checked;
                                setSendAssessment(next);
                                if (!next) {
                                  setSendFullAssessment(false);
                                  setSendShortAssessment(false);
                                } else if (!sendFullAssessment && !sendShortAssessment) {
                                  setSendFullAssessment(true);
                                }
                              }}
                              classes={{ switchBase: classes.switchBase, track: classes.switchTrack }}
                            />
                          }
                          label={sendAssessment ? 'On' : 'Off'}
                        />
                      </Box>
            
                      {sendAssessment && (
                        <Box className={classes.assessmentOptions}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!sendFullAssessment}
                                onChange={(e) => {
                                  setSendFullAssessment(e.target.checked);
                                  if (e.target.checked) {
                                    setSendShortAssessment(false);
                                  }
                                }}
                                className={classes.checkbox}
                              />
                            }
                            label="Send full assessment link by email"
                            classes={{ label: classes.checkboxLabel }}
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!!sendShortAssessment}
                                onChange={(e) => {
                                  setSendShortAssessment(e.target.checked);
                                  if (e.target.checked) {
                                    setSendFullAssessment(false);
                                  }
                                }}
                                className={classes.checkbox}
                              />
                            }
                            label="Send short-form assessment link by email"
                            classes={{ label: classes.checkboxLabel }}
                          />
                        </Box>
                      )}
            
                      {sendAssessment && (sendFullAssessment || sendShortAssessment) && (
                        <Box className={classes.contentSection} mt={2} mb={0}>
                          <Typography className={classes.label}>
                            Patient Email *
                          </Typography>
                          <TextField
                            fullWidth
                            type="email"
                            variant="outlined"
                            value={patientEmail}
                            onChange={(e) => setPatientEmail(e.target.value)}
                            className={classes.orangeFocused}
                            placeholder="patient@example.com"
                            error={!!error && !patientEmail.trim()}
                          />
                          <Typography className={classes.helperText}>
                            The assessment link will be sent to this email address.
                          </Typography>
                        </Box>
                      )}
                    </Box>
            
                    {error && (
                      <Box mt={2}>
                        <Typography className={classes.errorText}>
                          {error}
                        </Typography>
                      </Box>
                    )}
          </>
        )}

        {!isEditMode && (
          <>
            <Typography className={classes.sectionLabel}>Archived Treatment Courses</Typography>
            <Box className={classes.sectionPanel} mb={3}>
              {archivedTreatmentsLoading ? (
                <CircularProgress size={20} />
              ) : error ? (
                <Typography className={classes.errorText}>{error}</Typography>
              ) : archivedTreatments.length === 0 ? (
                <Typography className={classes.helperText}>No archived treatment courses.</Typography>
              ) : archivedTreatments.map((archivedTreatment) => (
                <Box key={archivedTreatment.id} display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                  <Typography variant="body2" style={{ color: '#002D55' }}>
                    {archivedTreatment.display_name || archivedTreatment.name || 'Untitled treatment'}
                  </Typography>
                  <Tooltip title="Reactivate treatment" arrow>
                    <span>
                      <IconButton
                        aria-label="reactivate treatment"
                        onClick={() => handleReactivate(archivedTreatment)}
                        disabled={submitting || !onReactivate}
                        size="small"
                      >
                        <UnarchiveIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </>
        )}

        <Box className={classes.noteBox}>
          <Typography variant="body2" style={{ color: '#002D55' }}>
            <strong>Note:</strong>{' '}
            {isEditMode
              ? 'Changing the treatment name will not change the diagnosis.'
              : selectedDiagnosis
                ? 'The selected diagnosis template will be used to create this treatment.'
                : 'Treatment will start with an empty journal that can be updated when the patient completes an assessment or manually by the physio.'}
          </Typography>
        </Box>


        {error && (
          <Box mt={2}>
            <Typography className={classes.errorText}>
              {error}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={classes.dialogActions}>
        {isEditMode ? (
          <Box>
            <Tooltip title="Archive treatment" arrow>
              <span>
                <IconButton
                  aria-label="archive treatment"
                  onClick={handleArchive}
                  disabled={submitting || !onArchive}
                >
                  <ArchiveIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete treatment" arrow>
              <span>
                <IconButton
                  aria-label="delete treatment"
                  onClick={handleDelete}
                  disabled={submitting || !onDelete}
                  className={classes.deleteButton}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ) : <span />}
        <Box className={classes.actionGroup}>
          <Button
            onClick={handleCancel}
            disabled={submitting}
            variant="outlined"
            className={classes.cancelButton}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !treatmentName.trim()}
            className={classes.confirmButton}
          >
            {submitting ? <CircularProgress size={20} style={{ color: '#fff' }} /> : (isEditMode ? 'Save Changes' : 'Create Treatment')}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default AddTreatmentDialog;
